import React from 'react'
import { Layout, Row, Col, Card, Button, Typography, Spin, Tag } from 'antd'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRightOutlined, HeartOutlined, SafetyOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { productsApi, articlesApi, siteSettingsApi } from '../../services/api'
import PopupNotice from '../../components/PopupNotice'
import SafeHtmlRenderer from '../../components/SafeHtmlRenderer'

const { Content } = Layout
const { Title, Paragraph } = Typography

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language as 'en' | 'zh'

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Fetch featured products
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['featuredProducts'],
    queryFn: () => productsApi.getAll({ featured: true, limit: 3, sortBy: 'sortOrder', order: 'asc' })
  })

  // Fetch recent articles
  const { data: articlesData, isLoading: articlesLoading } = useQuery({
    queryKey: ['recentArticles'],
    queryFn: () => articlesApi.getAll({ limit: 3, sortBy: 'publishedAt', order: 'desc' })
  })

  const settings = siteSettingsData?.settings
  const products = productsData?.products || []
  const articles = articlesData?.articles || []

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string } | undefined, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }

  // Helper function to filter out generic import-generated content
  const filterGenericContent = (content: string) => {
    if (!content) return ''
    
    // Remove the generic OMRON Healthcare description pattern
    const genericPattern = /Model:\s*[A-Za-z0-9-]+\s*\n\nThis is a high-quality medical device from OMRON Healthcare\. For detailed specifications and features, please refer to the product manual or contact our support team\./g
    
    return content.replace(genericPattern, '').trim()
  }

  // Default icon mapping for features
  const iconMap: { [key: string]: any } = {
    'HeartOutlined': HeartOutlined,
    'SafetyOutlined': SafetyOutlined,
    'ThunderboltOutlined': ThunderboltOutlined,
  }

  return (
    <>
      <Helmet>
        <title>
          {(() => {
            const seoTitle = getLocalizedText(settings?.seo?.defaultTitle);
            const siteName = getLocalizedText(settings?.siteName, 'MedCareHub');
            
            // If SEO title exists and contains the current site name, use it
            if (seoTitle && seoTitle.includes(siteName)) {
              return seoTitle;
            }
            
            // Otherwise, generate a dynamic title using current site name
            const heroTitle = getLocalizedText(settings?.hero?.title, t('heroTitle'));
            return `${heroTitle} - ${siteName}`;
          })()}
        </title>
        <meta name="description" content={getLocalizedText(settings?.seo?.defaultDescription, t('heroSubtitle'))} />
        <meta name="keywords" content={getLocalizedText(settings?.seo?.keywords, 'health, medical, products')} />
      </Helmet>

      {/* Popup Notice */}
      {settings?.popupNotice && (
        <PopupNotice
          isActive={settings.popupNotice.isActive}
          title={settings.popupNotice.title}
          description={settings.popupNotice.description}
          imageUrl={settings.popupNotice.imageUrl}
          frequency={settings.popupNotice.frequency}
        />
      )}

      <Content>
        {/* Hero Section - Dynamic Content from Admin */}
        <section 
          className="relative bg-gradient-to-r from-blue-600 to-blue-800 h-[75vh]"
          style={{
            backgroundImage: (() => {
              const bg = settings?.hero?.background?.[currentLang];
              // Prefer localized image if selected
              if (bg?.mediaType === 'image') {
                const img = bg?.imageUrl || settings?.hero?.backgroundImage;
                return img ? `url(${img})` : undefined;
              }
              // Fallback to legacy background image when no localized bg or not image
              const legacy = settings?.hero?.backgroundImage;
              return legacy ? `url(${legacy})` : undefined;
            })(),
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {(() => {
            const bg = settings?.hero?.background?.[currentLang];
            if (bg?.mediaType === 'video' && bg?.videoUrl) {
              return (
                <video
                  src={bg.videoUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
              );
            }
            return null;
          })()}
        </section>

        {/* Featured Products Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
                {t('home.sections.featuredProducts')}
              </Title>
              <Paragraph className="text-lg text-gray-600">
                {t('home.sections.featuredProductsSubtitle')}
              </Paragraph>
            </div>
            {productsLoading ? (
              <div className="text-center py-12">
                <Spin size="large" />
              </div>
            ) : (
              <Row gutter={[24, 24]}>
                {products.slice(0, 3).map((product: any) => (
                  <Col xs={24} md={8} key={product._id}>
                    <Card
                      hoverable
                      cover={
                        <div className="h-64 bg-white flex items-center justify-center p-4">
                          {product.images?.[0]?.url ? (
                            <img 
                              src={product.images[0].url} 
                              alt={product.images[0].alt?.en || product.name.en}
                              className="w-full h-full object-contain rounded"
                            />
                          ) : (
                            <span className="text-gray-400">{t('common.noImage')}</span>
                          )}
                        </div>
                      }
                      className="h-full"
                    >
                      <div className="mb-4">
                        <Title level={4} className="mb-2 line-clamp-2">
                          {getLocalizedText(product.name, 'Product')}
                        </Title>
                        <SafeHtmlRenderer
                          htmlContent={filterGenericContent(getLocalizedText(product.description, 'Product description'))}
                          className="text-gray-600 line-clamp-3"
                          tag="div"
                        />
                      </div>
                      <div className="mt-4 text-center">
                        <Button type="primary" ghost>
                          <Link to={`/products/${product._id}`}>
                            {t('common.learnMore')}
                          </Link>
                        </Button>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
            <div className="text-center mt-12">
              <Button type="primary" size="large">
                <Link to="/products">
                  {t('products.actions.viewAll')} <ArrowRightOutlined />
                </Link>
              </Button>
            </div>
          </div>
        </section>



        {/* Latest Articles Section */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
                {t('home.sections.latestArticles')}
              </Title>
              <Paragraph className="text-lg text-gray-600">
                {t('home.sections.latestArticlesSubtitle')}
              </Paragraph>
            </div>
            {articlesLoading ? (
              <div className="text-center py-12">
                <Spin size="large" />
              </div>
            ) : (
              <Row gutter={[24, 24]}>
                {articles.slice(0, 3).map((article: any) => (
                  <Col xs={24} md={8} key={article._id}>
                    <Card
                      hoverable
                      cover={
                        <div className="h-64 bg-white flex items-center justify-center p-4">
                          {(() => {
                            const localized = article.featuredImage?.[currentLang]?.url;
                            const legacy = article.featuredImage?.url;
                            const src = localized || legacy;
                            const alt = article.featuredImage?.[currentLang]?.alt || article.featuredImage?.alt?.[currentLang] || article.title?.[currentLang] || article.title?.en;
                            if (src) {
                              return (
                                <img 
                                  src={src}
                                  alt={alt}
                                  className="w-full h-full object-contain rounded"
                                />
                              );
                            }
                            return (
                            <span className="text-gray-400">{t('common.noImage')}</span>
                            );
                          })()}
                        </div>
                      }
                      className="h-full"
                    >
                      {article.category && (
                        <div className="mb-2">
                          <Tag color="blue">{getLocalizedText(article.category, 'Category')}</Tag>
                        </div>
                      )}
                      <Card.Meta
                        title={getLocalizedText(article.title, 'Article')}
                        description={getLocalizedText(article.excerpt, 'Article excerpt')}
                      />
                      <div className="mt-4">
                        <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                          <span>{t('blog.meta.author', { author: article.author?.name || 'Admin' })}</span>
                          <span>{new Date(article.publishedAt).toLocaleDateString()}</span>
                        </div>
                        <Link to={`/blog/${article._id}`}>
                          <Button type="primary" ghost block>
                            {t('common.readMore')}
                          </Button>
                        </Link>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
            <div className="text-center mt-12">
              <Button type="primary" size="large">
                <Link to="/blog">
                  {t('blog.categories.all')} <ArrowRightOutlined />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section - Dynamic Content from Admin */}
        {settings?.features && settings.features.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="max-w-6xl mx-auto px-4">
              <Title level={2} className="text-center mb-12">
                {t('home.sections.whyChooseUs')}
              </Title>
              <Row gutter={[32, 32]}>
                {settings.features.filter((f: any) => f.isActive).map((feature: any, index: number) => {
                  const IconComponent = iconMap[feature.icon] || HeartOutlined
                  return (
                    <Col xs={24} md={8} key={index}>
                      <Card className="text-center h-full border-0 shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-blue-600 mb-4">
                          <IconComponent style={{ fontSize: '3rem' }} />
                        </div>
                        <Title level={4} className="mb-3">
                          {getLocalizedText(feature.title, `Feature ${index + 1}`)}
                        </Title>
                        <Paragraph className="text-gray-600">
                          {getLocalizedText(feature.description, `Feature ${index + 1} description`)}
                        </Paragraph>
                      </Card>
                    </Col>
                  )
                })}
              </Row>
            </div>
          </section>
        )}


        {/* OMRON connect Section (now driven by site settings) */}
        {(() => {
          const oc = settings?.omronConnect;
          // Use current language only; if missing, show default color
          const bannerUrl = oc?.banner?.[currentLang]?.imageUrl || '';
          const title = getLocalizedText(oc?.title, t('home.omronConnect.title'));
          const subtitle = getLocalizedText(oc?.subtitle, t('home.omronConnect.subtitle'));
          const buttonText = getLocalizedText(oc?.buttonText, t('home.omronConnect.button'));
          const link = oc?.buttonLink || 'https://www.omronconnect.com/au/en_gb/';
          return (
            <section className="py-20 text-white relative" style={{ backgroundColor: bannerUrl ? 'transparent' : 'var(--primary-blue)' }}>
              {bannerUrl && (
                <div className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: `url('${bannerUrl}')` }} />
              )}
              <div className="absolute inset-0" style={{ backgroundColor: bannerUrl ? 'rgba(0,0,0,0.35)' : 'transparent' }} />
              <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
                <Title level={2} className="text-white mb-6" style={{ fontSize: '2.5rem', color: 'white' }}>
                  {title}
                </Title>
                <Paragraph className="text-xl text-white mb-8 max-w-3xl mx-auto opacity-90">
                  {subtitle}
                </Paragraph>
                <a href={link} target="_blank" rel="noopener noreferrer">
                  <Button 
                    type="primary" 
                    size="large" 
                    icon={<ArrowRightOutlined />}
                    className="bg-white text-blue-600 border-none hover:bg-gray-100"
                    style={{ backgroundColor: 'white', color: 'var(--primary-blue)', borderColor: 'white' }}
                  >
                    {buttonText}
                  </Button>
                </a>
              </div>
            </section>
          );
        })()}


      </Content>
    </>
  )
}

export default HomePage 